<template>
  <div class="min-h-full bg-[#f5f8fc] p-5 md:p-8">
    <section class="mb-6 rounded-[24px] border border-[#dbe5f3] bg-gradient-to-br from-white via-[#f8fbff] to-[#e9f2ff] px-7 py-8 shadow-sm">
      <h1 class="text-4xl font-extrabold tracking-tight text-[#14213d]">Client Sidenav Management</h1>
      <p class="mt-3 max-w-2xl text-sm text-[#64748b]">Review client account access, service plans, and sidebar permissions.</p>
    </section>

    <section class="mb-6 grid gap-4 md:grid-cols-3">
      <article v-for="card in summaryCards" :key="card.label" class="rounded-[20px] border border-white bg-white p-5 shadow-sm">
        <p class="text-xs font-extrabold uppercase tracking-wider text-[#71809a]">{{ card.label }}</p>
        <strong class="mt-3 block text-3xl font-extrabold text-[#1d2c47]">{{ card.value }}</strong>
        <p class="mt-1 text-sm text-[#64748b]">{{ card.detail }}</p>
      </article>
    </section>

    <section class="mb-6 flex flex-col gap-4 rounded-[20px] border border-[#dbe5f3] bg-white p-4 md:flex-row md:items-end">
      <label class="flex-1 text-xs font-extrabold uppercase tracking-wider text-[#71809a]">
        Search client
        <input v-model="searchQuery" class="mt-2 h-11 w-full rounded-xl border border-[#d5dfed] px-4 text-sm font-normal normal-case outline-none focus:border-blue-400" placeholder="Search name or email...">
      </label>
      <label class="w-full text-xs font-extrabold uppercase tracking-wider text-[#71809a] md:w-56">
        Status
        <select v-model="statusFilter" class="mt-2 h-11 w-full rounded-xl border border-[#d5dfed] px-3 text-sm font-normal normal-case outline-none">
          <option value="">All statuses</option>
          <option v-for="status in statusOptions" :key="status" :value="status">{{ status }}</option>
        </select>
      </label>
      <label class="w-full text-xs font-extrabold uppercase tracking-wider text-[#71809a] md:w-56">
        Service plan
        <select v-model="planFilter" class="mt-2 h-11 w-full rounded-xl border border-[#d5dfed] px-3 text-sm font-normal normal-case outline-none">
          <option value="">All service plans</option>
          <option v-for="plan in planOptions" :key="plan" :value="plan">{{ plan }}</option>
        </select>
      </label>
      <label class="w-full text-xs font-extrabold uppercase tracking-wider text-[#71809a] md:w-32">
        Rows
        <select v-model.number="pageSize" class="mt-2 h-11 w-full rounded-xl border border-[#d5dfed] px-3 text-sm font-normal normal-case outline-none">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
        </select>
      </label>
      <span class="pb-3 text-xs font-semibold text-[#64748b]">{{ filteredClients.length }} results</span>
    </section>

    <div class="mb-5 flex flex-wrap gap-2 rounded-[20px] border border-[#dbe5f3] bg-white p-2 shadow-sm">
      <button
        v-for="tab in serviceTabs"
        :key="tab.value"
        class="rounded-xl px-4 py-2 text-sm font-bold transition"
        :class="activeTab === tab.value ? 'bg-[#2878f0] text-white shadow-sm' : 'text-[#64748b] hover:bg-[#eef5ff]'"
        @click="activeTab = tab.value"
      >
        {{ tab.label }}
        <span class="ml-1 opacity-75">{{ tab.count }}</span>
      </button>
    </div>

    <section class="overflow-hidden rounded-[20px] border border-[#dbe5f3] bg-white shadow-sm">
      <div class="border-b border-[#e6edf6] px-5 py-5">
        <h2 class="text-lg font-extrabold text-[#172033]">Client accounts</h2>
        <p class="mt-1 text-sm text-[#71809a]">Manage sidebar access for each client account.</p>
      </div>
      <div v-if="filteredClients.length" class="overflow-x-auto">
        <table class="min-w-full divide-y divide-[#e6edf6] text-left">
          <thead class="bg-[#f8fbff]">
            <tr><th v-for="heading in ['Account', 'Service Plan', 'Status', 'Actions']" :key="heading" class="px-5 py-3 text-xs font-extrabold uppercase tracking-wider text-[#71809a]">{{ heading }}</th></tr>
          </thead>
          <tbody class="divide-y divide-[#eef2f7]">
            <tr v-for="client in paginatedClients" :key="client.id" class="hover:bg-[#f8fbff]">
              <td class="px-5 py-4"><div class="font-bold text-[#172033]">{{ client.company_name || 'Unnamed client' }}</div><div class="text-sm text-[#71809a]">{{ client.email || 'No email' }}</div></td>
              <td class="px-5 py-4"><span :class="servicePlanClass(client)" class="inline-flex rounded-full px-3 py-1 text-xs font-bold">{{ servicePlanLabel(client) }}</span></td>
              <td class="px-5 py-4"><span :class="statusClass(client)" class="rounded-full px-3 py-1 text-xs font-bold">{{ client.client_account_status || client.account_status || 'Unknown' }}</span></td>
              <td class="px-5 py-4 text-right"><button v-if="isSuperAdmin" class="rounded-xl bg-[#2878f0] px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-[#1767dc]" @click="openAssignModal(client)">Manage Sidenav</button></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="px-5 py-12 text-center text-sm text-[#71809a]">No clients found.</div>
      <div v-if="filteredClients.length" class="flex flex-col gap-3 border-t border-[#e6edf6] px-5 py-4 text-sm text-[#64748b] sm:flex-row sm:items-center sm:justify-between">
        <span>Showing {{ pageStart }}–{{ pageEnd }} of {{ filteredClients.length }}</span>
        <div class="flex items-center gap-2">
          <button class="rounded-lg border border-[#d5dfed] px-3 py-2 font-semibold disabled:cursor-not-allowed disabled:opacity-40" :disabled="currentPage === 1" @click="currentPage--">Previous</button>
          <span class="px-2 font-semibold">Page {{ currentPage }} of {{ totalPages }}</span>
          <button class="rounded-lg border border-[#d5dfed] px-3 py-2 font-semibold disabled:cursor-not-allowed disabled:opacity-40" :disabled="currentPage === totalPages" @click="currentPage++">Next</button>
        </div>
      </div>
    </section>

    <!-- Modal for Assigning Sidenav Items -->
    <ClientAccessOverridesModal
      v-if="isSuperAdmin && showModal"
      :open="showModal"
      :client="selectedClient"
      @update:open="showModal = $event"
      @saved="closeModal"
    />
  </div>
</template>

<script>
import { computed, ref, onMounted, watch } from "vue";
import { storeToRefs } from "pinia";
import { getClients } from "@/services/clientService"; // you'll create this
import { getSidenavItems } from "@/services/sidenavService";
import http from "@/services/http";

import {
  getClientSidenavItems,
  saveClientSidenavItems,
} from "@/services/clientSidenavService";

import ClientAccessOverridesModal from "@/components/dashboard/clients/ClientAccessOverridesModal.vue";
import Swal from "sweetalert2";
import { useAuthStore } from "@/store/auth";

export default {
  name: "ManageClientSidenav",
  components: {
    ClientAccessOverridesModal,
  },
  setup() {
    const authStore = useAuthStore();
    const { isSuperAdmin } = storeToRefs(authStore);
    const clients = ref([]);
    const sidenavItems = ref([]);
    const selectedClient = ref(null);
    const assignedSidenavIds = ref([]);
    const showModal = ref(false);
    const microappClientIds = ref(new Set());
    const searchQuery = ref("");
    const statusFilter = ref("");
    const planFilter = ref("");
    const currentPage = ref(1);
    const pageSize = ref(10);
    const activeTab = ref("dmp");

    const serviceTabs = computed(() => [
      { value: "dmp", label: "Digital Marketing Partner", count: clients.value.filter((client) => servicePlanLabel(client) === "Digital Marketing Partner").length },
      { value: "microapps", label: "Microapps", count: clients.value.filter((client) => ["Microapps", "AI-Powered Microapp"].includes(servicePlanLabel(client))).length },
      { value: "website-leads", label: "Website Lead Management", count: clients.value.filter((client) => servicePlanLabel(client) === "Website Lead Management").length },
    ]);

    const statusOptions = computed(() => [...new Set(clients.value.map((client) => client.client_account_status || client.account_status || "Unknown"))].sort());
    const planOptions = computed(() => [...new Set(clients.value.map(servicePlanLabel).filter(Boolean))].sort());
    const filteredClients = computed(() => {
      const query = searchQuery.value.trim().toLowerCase();
      return clients.value.filter((client) => {
        const status = client.client_account_status || client.account_status || "Unknown";
        const plan = servicePlanLabel(client);
        const matchesTab = activeTab.value === "dmp"
          ? plan === "Digital Marketing Partner"
          : activeTab.value === "microapps"
            ? ["Microapps", "AI-Powered Microapp"].includes(plan)
            : plan === "Website Lead Management";
        const matchesQuery = !query || `${client.company_name || ""} ${client.email || ""}`.toLowerCase().includes(query);
        return matchesTab && matchesQuery
          && (!statusFilter.value || status === statusFilter.value)
          && (!planFilter.value || plan === planFilter.value);
      });
    });
    const totalPages = computed(() => Math.max(1, Math.ceil(filteredClients.value.length / pageSize.value)));
    const paginatedClients = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value;
      return filteredClients.value.slice(start, start + pageSize.value);
    });
    const pageStart = computed(() => filteredClients.value.length ? ((currentPage.value - 1) * pageSize.value) + 1 : 0);
    const pageEnd = computed(() => Math.min(currentPage.value * pageSize.value, filteredClients.value.length));
    const summaryCards = computed(() => [
      { label: "All clients", value: clients.value.length, detail: "Client accounts" },
      { label: "Active", value: clients.value.filter((client) => (client.client_account_status || client.account_status) === "Active").length, detail: "Active accounts" },
      { label: "Service plans", value: new Set(clients.value.map(servicePlan).filter(Boolean)).size, detail: "Plans represented" },
    ]);

    watch([searchQuery, statusFilter, planFilter, pageSize, activeTab], () => {
      currentPage.value = 1;
    });

    watch(totalPages, (pages) => {
      if (currentPage.value > pages) currentPage.value = pages;
    });

    const fetchClients = async () => {
      const data = await getClients();
      clients.value = data.data;
    };

    const fetchSidenavItems = async () => {
      const data = await getSidenavItems();
      sidenavItems.value = data.filter((item) => item.is_active);
    };

    const fetchMicroappAccounts = async () => {
      try {
        const response = await http.get("/api/microapps");
        const records = Array.isArray(response.data?.data) ? response.data.data : [];
        microappClientIds.value = new Set(
          records.map((record) => String(record.client_id || record.dmsuite_client_id || "")).filter(Boolean)
        );
      } catch (error) {
        console.error("Unable to load Microapp account links", error);
      }
    };

    const servicePlan = (client) => {
      return client.service_plan?.name
        || client.service_plan_name
        || client.client_package?.package?.service_plan?.name
        || client.clientPackage?.package?.servicePlan?.name
        || client.package?.name
        || "No service plan";
    };

    const serviceIncludes = (client, term) => {
      const packageServices = (client.assigned_packages || []).flatMap((item) => [
        item.name,
        ...(item.services || []).map((service) => service.name || service),
      ]);
      const services = [
        ...(Array.isArray(client.services) ? client.services : []),
        ...(client.assigned_services || []).map((service) => service.name || service.service?.name || service),
        ...packageServices,
      ];
      return services.some((service) => String(service || "").toLowerCase().includes(term));
    };

    const servicePlanLabel = (client) => {
      if (microappClientIds.value.has(String(client.id))) {
        return "Microapps";
      }
      if (microappClientIds.value.has(String(client.client_id || ""))) {
        return "Microapps";
      }
      const assignedPlan = client.service_plan || client.assigned_packages?.[0]?.service_plan;
      const planCode = String(assignedPlan?.code || "").toLowerCase();
      const planName = String(assignedPlan?.name || "").toLowerCase();

      if (planCode === "ai_micro_app" || planName.includes("ai-powered micro")) {
        return "AI-Powered Microapp";
      }
      if (planCode === "website_lead_capture" || planName.includes("website lead")) {
        return "Website Lead Management";
      }
      if (planCode === "dmp" || planName.includes("digital marketing partner")) {
        return "Digital Marketing Partner";
      }

      const serviceNames = [
        ...(Array.isArray(client.services) ? client.services : []),
        ...(client.assigned_services || []).map((service) => service.name || service.service?.name || service),
        ...(client.assigned_packages || []).map((item) => item.name),
        ...(client.assigned_packages || []).flatMap((item) => (item.services || []).map((service) => service.name || service)),
      ].map((service) => String(service || "").toLowerCase());

      if (serviceNames.some((name) => name.includes("ai powered micro app"))) {
        return "AI-Powered Microapp";
      }
      if (serviceNames.some((name) => name.includes("microapp") || name.includes("micro app"))) {
        return "Microapps";
      }
      if (serviceNames.some((name) => name.includes("website lead capture"))) {
        return "Website Lead Management";
      }

      return client.service_plan?.name
        || client.service_plan_name
        || client.client_package?.package?.service_plan?.name
        || client.clientPackage?.package?.servicePlan?.name
        || client.package?.name
        || "Digital Marketing Partner";
    };

    const servicePlanClass = (client) => {
      const label = servicePlanLabel(client);
      if (label === "Microapps" || label === "AI-Powered Microapp") {
        return "bg-violet-50 text-violet-700";
      }
      if (label === "Digital Marketing Partner") {
        return "bg-blue-50 text-blue-700";
      }
      if (label === "Website Lead Management") {
        return "bg-amber-50 text-amber-700";
      }
      return "bg-slate-100 text-slate-600";
    };

    const statusClass = (client) => {
      const status = String(client.client_account_status || client.account_status || "unknown").toLowerCase();
      if (status === "active") return "bg-emerald-50 text-emerald-700";
      if (status.includes("hold")) return "bg-amber-50 text-amber-700";
      if (status.includes("grace") || status.includes("activation")) return "bg-blue-50 text-blue-700";
      if (status.includes("offboard") || status.includes("inactive") || status.includes("expired")) return "bg-rose-50 text-rose-700";
      return "bg-slate-100 text-slate-600";
    };

    const openAssignModal = async (client) => {
      if (!isSuperAdmin.value) return;
      selectedClient.value = client;
      showModal.value = true;

      const assignments = await getClientSidenavItems(client.id);
      assignedSidenavIds.value = assignments.map((a) => a.sidenav_item_id);
    };

    const closeModal = () => {
      showModal.value = false;
      selectedClient.value = null;
      assignedSidenavIds.value = [];
    };

    const saveAssignments = async (selectedIds) => {
      await saveClientSidenavItems(selectedClient.value.id, selectedIds);
      Swal.fire("Success!", "Client Sidenav Access updated.", "success");
      closeModal();
    };

    onMounted(() => {
      fetchClients();
      fetchSidenavItems();
      fetchMicroappAccounts();
    });

    return {
      clients,
      sidenavItems,
      selectedClient,
      assignedSidenavIds,
      showModal,
      isSuperAdmin,
      openAssignModal,
      closeModal,
      saveAssignments,
      servicePlan,
      servicePlanLabel,
      servicePlanClass,
      statusClass,
      serviceIncludes,
      searchQuery,
      statusFilter,
      planFilter,
      statusOptions,
      planOptions,
      filteredClients,
      summaryCards,
      currentPage,
      pageSize,
      totalPages,
      paginatedClients,
      pageStart,
      pageEnd,
      activeTab,
      serviceTabs,
    };
  },
};
</script>
